/** @jsxImportSource react */
import { useEffect } from 'react'
import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { emailRepliesAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { selectIsTenantAdmin } from '../../store/slices/tenantSlice'
import { getErrorMessage } from '../../utils/apiErrors'
import { formatRelativeTime } from '../../utils/helpers'
import { useNotify } from '../../components/common/NotificationProvider'
import PageHeader from '../../components/common/PageHeader'
import Loading from '../../components/common/Loading'

const NAMES = { GOOGLE: 'Gmail / Google Workspace', MICROSOFT: 'Outlook / Microsoft 365' }
const CAL_NAMES = { GOOGLE: 'Google Calendar', MICROSOFT: 'Outlook calendar' }
const RESULT = {
  connected: ['success', 'Connected. Replies and calendar changes show up in the CRM within a few minutes.'],
  cancelled: ['info', 'Connecting was cancelled.'],
  failed: ['error', 'The mailbox couldn’t be connected; try again.'],
  invalid: ['error', 'That link expired; try connecting again.'],
  'no-offline-access': ['warning', 'The provider didn’t give lasting access; try connecting again.'],
}

const ComingSoonRow = ({ name }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between"
    sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 500 }} color="text.secondary">{name}</Typography>
      <Typography variant="caption" color="text.secondary">Available soon</Typography>
    </Box>
    <Chip size="small" variant="outlined" label="Coming soon" />
  </Stack>
)

/** Replies to emails sent from records: how they come back here, and your own mailbox connection. */
const EmailRepliesPage = () => {
  const tenantId = useTenantKey()
  const isAdmin = useSelector(selectIsTenantAdmin)
  const queryClient = useQueryClient()
  const notify = useNotify()
  const [params, setParams] = useSearchParams()
  const status = useQuery({ queryKey: [tenantId, 'email-replies', 'status'], queryFn: emailRepliesAPI.status })
  const mailboxes = useQuery({ queryKey: [tenantId, 'email-replies', 'mailboxes'], queryFn: emailRepliesAPI.mailboxes })
  const connect = useMutation({ mutationFn: ({ provider, purpose }) => emailRepliesAPI.connect(provider, purpose), onSuccess: ({ url }) => window.location.assign(url), onError: (e) => notify(getErrorMessage(e), 'error') })
  const disconnect = useMutation({
    mutationFn: emailRepliesAPI.disconnect,
    onSuccess: () => { notify('Mailbox disconnected'); queryClient.invalidateQueries({ queryKey: [tenantId, 'email-replies'] }) },
  })
  const result = RESULT[params.get('mail')]
  useEffect(() => {
    if (params.get('mail') === 'connected') queryClient.invalidateQueries({ queryKey: [tenantId, 'email-replies'] })
  }, [params, queryClient, tenantId])

  if (status.isLoading || mailboxes.isLoading) return <Loading />
  if (status.error) return <Alert severity="error">{getErrorMessage(status.error)}</Alert>
  const st = status.data
  const mine = mailboxes.data?.mine || []
  const soon = st.comingSoon || []   // e.g. Google, while it reviews the app's Gmail / Calendar access
  const providers = [...st.mailboxProviders, ...soon]
  const routes = [
    st.replyAddress && 'replies go to the CRM’s reply address',
    st.webhook && 'a mail provider forwards them',
    st.imap && 'a shared mailbox is read every minute',
    st.mailboxProviders.length > 0 && 'members can connect their own mailbox',
  ].filter(Boolean)

  return (
    <Box sx={{ maxWidth: 820 }}>
      <PageHeader title="Email & calendar" subtitle="Customer replies on the record’s timeline, and meetings in your own calendar." />
      {result && <Alert severity={result[0]} onClose={() => setParams({}, { replace: true })} sx={{ mb: 2 }}>{result[1]}</Alert>}

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Your mailbox</Typography>
        {providers.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {st.replyAddress || st.imap || st.webhook
              ? 'Nothing to do: replies already reach the CRM through the workspace’s reply address.'
              : 'Connecting a mailbox isn’t set up on this installation.'}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Connect the mailbox you send from, so replies that land in it are copied to the record. Only replies to emails sent from
              the CRM are read; nothing else in your mailbox is opened.
            </Typography>
            {providers.map((p) => {
              const c = mine.find((x) => x.provider === p && x.purposes.includes('MAIL'))
              if (soon.includes(p)) return <ComingSoonRow key={p} name={NAMES[p]} />
              return (
                <Stack key={p} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between"
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{NAMES[p]}</Typography>
                    {c ? (
                      <Typography variant="caption" color={c.status === 'ERROR' ? 'error' : 'text.secondary'}>
                        {c.email} · {c.status === 'ERROR' ? c.error : `checked ${formatRelativeTime(c.syncedUntil)}`}
                      </Typography>
                    ) : <Typography variant="caption" color="text.secondary">Not connected</Typography>}
                  </Box>
                  <Stack direction="row" spacing={1}>
                    {c && <Chip size="small" color={c.status === 'ERROR' ? 'error' : 'success'} label={c.status === 'ERROR' ? 'Needs reconnecting' : 'Connected'} />}
                    {(!c || c.status === 'ERROR') && (
                      <Button size="small" variant="contained" disabled={connect.isPending} onClick={() => connect.mutate({ provider: p, purpose: 'MAIL' })}>Connect</Button>
                    )}
                    {c && <Button size="small" color="inherit" disabled={disconnect.isPending} onClick={() => disconnect.mutate(p)}>Disconnect</Button>}
                  </Stack>
                </Stack>
              )
            })}
          </Stack>
        )}
      </Paper>

      {providers.length > 0 && (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Your calendar</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Meetings you plan in the CRM go into your calendar, which sends the invites; moving or cancelling them there updates the
            CRM. Your other events appear in grey on the CRM calendar (only you see what they are), and teammates planning a meeting
            with you see when you’re busy, never what. Only meetings made in the CRM are changed. Without a connected calendar,
            invites go out by email.
          </Typography>
          <Stack spacing={1.5}>
            {providers.map((p) => {
              const c = mine.find((x) => x.provider === p && x.purposes.includes('CALENDAR'))
              if (soon.includes(p)) return <ComingSoonRow key={p} name={CAL_NAMES[p]} />
              return (
                <Stack key={p} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between"
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{CAL_NAMES[p]}</Typography>
                    <Typography variant="caption" color={c?.status === 'ERROR' ? 'error' : 'text.secondary'}>
                      {c ? `${c.email}${c.status === 'ERROR' ? ` · ${c.error}` : ''}` : 'Not connected'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    {c && <Chip size="small" color={c.status === 'ERROR' ? 'error' : 'success'} label={c.status === 'ERROR' ? 'Needs reconnecting' : 'Connected'} />}
                    {(!c || c.status === 'ERROR') && (
                      <Button size="small" variant="contained" disabled={connect.isPending} onClick={() => connect.mutate({ provider: p, purpose: 'CALENDAR' })}>Connect</Button>
                    )}
                  </Stack>
                </Stack>
              )
            })}
          </Stack>
        </Paper>
      )}

      {isAdmin && (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>How replies come back here</Typography>
          {routes.length ? (
            <Stack spacing={0.5}>
              {routes.map((r) => <Typography key={r} variant="body2">• {r[0].toUpperCase() + r.slice(1)}</Typography>)}
            </Stack>
          ) : (
            <Alert severity="info">
              Not set up yet: replies go only to the sender’s own inbox. The installation’s operator can switch on a reply address, a provider
              webhook (e.g. Brevo), a shared IMAP mailbox, or Gmail / Outlook connections (deploy/README, “Email replies”).
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  )
}

export default EmailRepliesPage
